import { math, Quat, Vec3 } from "playcanvas";

/**
 * Extracts the Euler angles equivalent to the rotational portion of the specified 4x4 matrix. The
 * returned Euler angles are in XYZ order an in degrees.
 *
 * Identical to pc.Mat4.getEulerAngles except that this version handles cases where scaleSign
 * of the matrix is negative (for flipped transforms).
 *
 * @param transform - A 4x4 matrix to extract Euler angles from.
 * @param eulers - A 3-d vector to receive the Euler angles.
 */
export const getEulerAngles = (function () {
	const scale = new Vec3();

	return function (transform: pc.Mat4, eulers = new Vec3()) {
		getScale(transform, scale);
		const sx = scale.x;
		const sy = scale.y;
		const sz = scale.z;

		if (sx === 0 || sy === 0 || sz === 0) {
			return eulers.set(0, 0, 0);
		}

		const m = transform.data;

		const y = Math.asin(-m[2] / sx);
		const halfPi = Math.PI * 0.5;

		let x, z;

		if (y < halfPi) {
			if (y > -halfPi) {
				x = Math.atan2(m[6] / sy, m[10] / sz);
				z = Math.atan2(m[1] / sx, m[0] / sx);
			} else {
				// Not a unique solution
				z = 0;
				x = -Math.atan2(m[4] / sy, m[5] / sy);
			}
		} else {
			// Not a unique solution
			z = 0;
			x = Math.atan2(m[4] / sy, m[5] / sy);
		}

		return eulers.set(x, y, z).mulScalar(math.RAD_TO_DEG);
	};
})();

/**
 * Extracts the rotational portion of the specified 4x4 matrix.
 *
 * Handles cases where scaleSign of the matrix is negative (for flipped transforms).
 *
 * @param transform - A 4x4 matrix to extract Euler angles from.
 * @param rotation - A quaternion to receive the rotation.
 */
export const getRotation = (function () {
	const eulerAngles = new Vec3();

	return function (transform: pc.Mat4, rotation = new Quat()) {
		getEulerAngles(transform, eulerAngles);
		return rotation.setFromEulerAngles(eulerAngles);
	};
})();

/**
 * Extracts the scale component from the specified 4x4 matrix.
 *
 * Identical to pc.Mat4.getScale except that this version detects if the matrix
 * is flipped / mirrored. In these cases, the X-component of the calculated scale is negated.
 */
export function getScale(transform: pc.Mat4, scale = new Vec3()) {
	transform.getScale(scale);

	if (transform.scaleSign < 0) {
		scale.x *= -1;
	}

	return scale;
}
